import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useAccount } from "wagmi";
import { Chains, SpaceBalanceOfABI } from "./data";
import { BigNumberish, ethers } from "ethers";
import { getJicAddress, onJicChange } from "../lib/jic";

import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectChain } from "../features/chain";
import { setAddress, setBalance } from "../features/account";
import { emptyEthAddress } from "../utils/util";
import BigNumber from "bignumber.js";
import { getInjectedWalletAddress, hasInjectedProvider } from "../lib/injectedWallet";

type TradeContextType = {
  reFetchBalance: () => void;
};

export const TradeContext = createContext<TradeContextType | null>(null);

type TradeContextProviderProps = {
  children: ReactNode;
};

export function TradeContextProvider({ children }: TradeContextProviderProps) {
  const { address: signerAddress } = useAccount();
  const [jicAddress, setJicAddress] = useState<string | null>(() =>
    getJicAddress()
  );
  const [injectedAddress, setInjectedAddress] = useState<string | null>(null);
  const address =
    jicAddress ||
    (signerAddress ? signerAddress.toLowerCase() : null) ||
    injectedAddress;

  const dispatch = useAppDispatch();
  const chain = useAppSelector(selectChain);

  useEffect(
    () =>
      onJicChange((user) => {
        setJicAddress(user?.sub ? user.sub.toLowerCase() : null);
      }),
    []
  );

  useEffect(() => {
    let live = true;
    const refreshInjectedAddress = async () => {
      if (!hasInjectedProvider()) {
        if (live) setInjectedAddress(null);
        return;
      }
      const walletAddress = await getInjectedWalletAddress({
        requestIfNeeded: false,
      }).catch(() => null);
      if (live) setInjectedAddress(walletAddress ? walletAddress.toLowerCase() : null);
    };
    refreshInjectedAddress();
    window.addEventListener("focus", refreshInjectedAddress);
    return () => {
      live = false;
      window.removeEventListener("focus", refreshInjectedAddress);
    };
  }, []);

  const readBalanceFromRpc = useCallback(async () => {
    if (!address) return null;
    const provider = new ethers.JsonRpcProvider(Chains[chain].provider);
    const contract = new ethers.Contract(
      Chains[chain].contractAddr,
      SpaceBalanceOfABI,
      provider
    );
    return (await contract.balanceOf(address)) as BigNumberish;
  }, [address, chain]);

  const updateBalance = useCallback(
    (balance: BigNumberish | null | undefined) => {
      if (balance === null || balance === undefined) {
        dispatch(setBalance("0.00"));
        return;
      }
      const balanceStr = ethers.formatEther(balance.toString());
      const readable = BigNumber(balanceStr);
      dispatch(setBalance(readable.gte(1) ? readable.toFixed(2) : readable.toFixed(6)));
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(setAddress(address ? address : emptyEthAddress));
  }, [address, dispatch]);

  const reFetchBalance = useCallback(async () => {
    if (!address) {
      dispatch(setBalance("0.00"));
      return;
    }

    try {
      updateBalance(await readBalanceFromRpc());
    } catch {
      dispatch(setBalance("0.00"));
    }
  }, [address, dispatch, readBalanceFromRpc, updateBalance]);

  useEffect(() => {
    reFetchBalance();
  }, [address, chain, reFetchBalance]);

  const value: TradeContextType = {
    reFetchBalance,
  };
  return (
    <TradeContext.Provider value={value}>{children}</TradeContext.Provider>
  );
}

export function useTrade(): TradeContextType {
  const context = useContext(TradeContext);
  if (!context)
    throw new Error("useTrade must be used within an TradeContext Provider");

  return context;
}
